import type { CalculationModel } from '../../calculationModel/calculationModelSchema';

import type {
  CalculationModelMatcherBinding,
  CalculationModelParameterExpression,
} from './entities';
import { extractVariableNamesFromExpression } from './expressionVariableUtils';
import {
  getAllVariablesForImportedDescendant,
  getAllVariablesForTopLevelCalculationModel,
  getMatcherBindings,
  getParameterExpressionsMap,
  type CalculationMethodVariable,
} from './calculationMethodVariables';

/**
 * Minimal interface for a parameter resolver containing expression mappings.
 * Matches the shape of `CalculationModelParameterResolver.parameterExpressions`.
 */
interface ParameterResolverLike {
  parameterExpressions?: Array<CalculationModelParameterExpression>;
}

/**
 * Minimal interface for an imported descendant model needed by the dependency graph.
 * Works with both entities and GraphQL types.
 */
export interface ImportedDescendantForGraph {
  calculationModel: CalculationModel;
  defaultParameterResolver?: ParameterResolverLike | null;
}

/**
 * Minimal interface for a top-level model record needed by the dependency graph.
 * Works with both entities and GraphQL types.
 */
export interface ModelRecordForGraph {
  calculationModel: CalculationModel;
  matchers: Array<{
    bindings: Array<CalculationModelMatcherBinding>;
  }>;
  defaultParameterResolver?: ParameterResolverLike | null;
  importedDescendants: Array<ImportedDescendantForGraph>;
}

/**
 * A pre-computed dependency graph for variables in a calculation method.
 * Maps each variable name to the set of variable names it directly references.
 */
export interface VariableDependencyGraph {
  /** Map from variable name to set of directly referenced variable names */
  dependencies: Map<string, Set<string>>;
  /** All variables in the method, keyed by name */
  variablesByName: Map<string, CalculationMethodVariable>;
}

/**
 * Builds the dependency graph by parsing all variable expressions once.
 * Called during store initialization for fast lookups later.
 */
export function buildDependencyGraph(
  calculationMethod: ModelRecordForGraph
): VariableDependencyGraph {
  const dependencies = new Map<string, Set<string>>();
  const variablesByName = new Map<string, CalculationMethodVariable>();

  // The graph is built once on method load so later lookups can be O(1) and
  // traversals can avoid re-parsing expressions.
  //
  // Precedence rule: top-level variables win over imported descendant variables
  // when there is a name collision.
  addVariablesToGraph({
    variables: getTopLevelVariables(calculationMethod),
    dependencies,
    variablesByName,
    shouldInclude: () => true,
  });

  // Get variables from each imported descendant
  for (const imported of calculationMethod.importedDescendants) {
    addVariablesToGraph({
      variables: getImportedVariables(imported),
      dependencies,
      variablesByName,
      // Only add if not already present (top-level takes precedence)
      shouldInclude: (variableName) => !variablesByName.has(variableName),
    });
  }

  return { dependencies, variablesByName };
}

/**
 * Adds variables to the graph and parses their direct dependencies.
 *
 * This keeps graph construction logic in one place so the top-level and imported
 * paths stay consistent.
 */
function addVariablesToGraph({
  variables,
  dependencies,
  variablesByName,
  shouldInclude,
}: {
  variables: Array<CalculationMethodVariable>;
  dependencies: Map<string, Set<string>>;
  variablesByName: Map<string, CalculationMethodVariable>;
  shouldInclude: (variableName: string) => boolean;
}): void {
  for (const variable of variables) {
    if (!shouldInclude(variable.name)) {
      continue;
    }

    variablesByName.set(variable.name, variable);
    dependencies.set(
      variable.name,
      extractVariableNamesFromExpression(variable.expressionColumnText)
    );
  }
}

/**
 * Gets all variables from the top-level calculation model.
 */
function getTopLevelVariables(
  calculationMethod: ModelRecordForGraph
): Array<CalculationMethodVariable> {
  const { calculationModel, matchers, defaultParameterResolver } =
    calculationMethod;

  const { localVariables, inputVariables, globalRefDataVariables } =
    getAllVariablesForTopLevelCalculationModel({
      calculationModel,
      parameterExpressions: getParameterExpressionsMap(
        defaultParameterResolver?.parameterExpressions
      ),
      matcherBindings: getMatcherBindings(matchers),
    });

  return [...localVariables, ...inputVariables, ...globalRefDataVariables];
}

/**
 * Gets all variables from an imported descendant.
 */
function getImportedVariables(
  imported: ImportedDescendantForGraph
): Array<CalculationMethodVariable> {
  const { calculationModel, defaultParameterResolver } = imported;

  const { localVariables, inputVariables, globalRefDataVariables } =
    getAllVariablesForImportedDescendant({
      calculationModel,
      parameterExpressions: getParameterExpressionsMap(
        defaultParameterResolver?.parameterExpressions
      ),
      importParamBindings: {},
    });

  return [...localVariables, ...inputVariables, ...globalRefDataVariables];
}

/**
 * Finds all variables transitively reachable from a set of starting variable names.
 * Uses the pre-built dependency graph for fast traversal.
 */
export function getTransitiveVariablesFromGraph(
  graph: VariableDependencyGraph,
  startingNames: Set<string>
): Array<CalculationMethodVariable> {
  const visited = new Set<string>();
  const result: Array<CalculationMethodVariable> = [];

  function visit(name: string): void {
    if (visited.has(name)) return;
    visited.add(name);

    const variable = graph.variablesByName.get(name);
    if (variable) {
      result.push(variable);
    }

    // Visit dependencies
    const deps = graph.dependencies.get(name);
    if (deps) {
      for (const dep of deps) {
        visit(dep);
      }
    }
  }

  // Start traversal from each starting name
  for (const name of startingNames) {
    visit(name);
  }

  return result;
}
